import { useCallback, useEffect, useRef, useState } from "react";
import { Box, Chip, Typography } from "@mui/material";
import FormInput from "../Common/FormInputs/MuiTextField";
import AppButton from "../Common/Button/Button";
import FormFieldRow from "./FormFieldRow";
import { getAssetMetadata, getAssetErrorMessage } from "../../action/asset";
import {
  mapMetadataEntriesToAssetTags,
  parseAssetMetadataResponse,
} from "../../utils/inAppNotificationUtils";
import {
  IN_APP_NOTIFICATION_VALIDATIONS,
  compactInputSx,
  pageFormLabelSx,
  pageFormShortInputSx,
  pageSubsectionInputRowSx,
  pageSubsectionLabelSx,
  pageFormInlineActionSx,
  pageAssetIdControlRowSx,
} from "../../constants/inAppNotificationConstant";

const AssetFields = ({
  assets,
  onChange,
  errors = {},
  required = false,
  idPrefix = "asset",
  feedbackHandler,
  compact = true,
  page = false,
  paired = false,
  readOnly = false,
}) => {
  const [lookupId, setLookupId] = useState(assets.asset ?? "");
  const [fetchLoading, setFetchLoading] = useState(false);
  const [lastFetchedAssetId, setLastFetchedAssetId] = useState("");
  const fetchInFlightRef = useRef(null);

  useEffect(() => {
    setLookupId(assets.asset ?? "");
  }, [assets.asset]);

  const fieldCompact = page ? false : compact;
  const inputSx = page ? pageFormShortInputSx : compactInputSx;

  const applySelectedTag = useCallback(
    (tagEntries, preferredTag = assets.assetTag) => {
      const tags = mapMetadataEntriesToAssetTags(tagEntries);
      onChange("tags", tags);

      if (!tags.length) {
        onChange("assetTag", "");
        onChange("resolutions", []);
        return;
      }

      const selected =
        tags.find((entry) => entry.assetTag === preferredTag) || tags[0];

      onChange("assetTag", selected.assetTag);
      onChange("resolutions", selected.resolutions);
    },
    [assets.assetTag, onChange],
  );

  const fetchMetadata = useCallback(
    async (assetId, { silent = false } = {}) => {
      const trimmedId = assetId?.trim();
      if (!trimmedId) {
        if (!silent) {
          feedbackHandler?.({ open: true, type: "error", msg: "Asset ID is required" });
        }
        return;
      }

      setFetchLoading(true);
      setLastFetchedAssetId(trimmedId);
      fetchInFlightRef.current = trimmedId;

      try {
        const response = await getAssetMetadata(trimmedId);
        const entries = parseAssetMetadataResponse(response?.data);

        if (!entries.length) {
          onChange("asset", "");
          onChange("tags", []);
          onChange("assetTag", "");
          onChange("resolutions", []);
          if (!silent) {
            feedbackHandler?.({
              open: true,
              type: "error",
              msg: "Asset not found. Please verify the Asset ID.",
            });
          }
          return;
        }

        onChange("asset", trimmedId);
        applySelectedTag(entries);

        if (!silent) {
          feedbackHandler?.({
            open: true,
            type: "success",
            msg: "Asset metadata fetched successfully",
          });
        }
      } catch (error) {
        onChange("asset", "");
        onChange("tags", []);
        onChange("assetTag", "");
        onChange("resolutions", []);
        if (!silent) {
          feedbackHandler?.({
            open: true,
            type: "error",
            msg: getAssetErrorMessage(
              error,
              "Failed to fetch asset metadata",
              "Asset not found. Please verify the Asset ID.",
            ),
          });
        }
      } finally {
        if (fetchInFlightRef.current === trimmedId) {
          fetchInFlightRef.current = null;
        }
        setFetchLoading(false);
      }
    },
    [applySelectedTag, feedbackHandler, onChange],
  );

  useEffect(() => {
    if (
      fetchLoading ||
      !assets.asset?.trim() ||
      lastFetchedAssetId === assets.asset ||
      fetchInFlightRef.current
    ) {
      return;
    }
    fetchMetadata(assets.asset, { silent: true });
  }, [assets.asset, fetchLoading, fetchMetadata, lastFetchedAssetId]);

  const handleFetchClick = () => {
    fetchMetadata(lookupId);
  };

  const preventBlurBeforeClick = (event) => {
    event.preventDefault();
  };

  const handleLookupIdChange = (value) => {
    setLookupId(value);
    onChange("asset", "");
    onChange("tags", []);
    onChange("assetTag", "");
    onChange("resolutions", []);
    setLastFetchedAssetId("");
  };

  const displayTags = assets.tags?.length
    ? assets.tags
    : assets.assetTag
      ? [{ assetTag: assets.assetTag, resolutions: assets.resolutions || [] }]
      : [];

  const assetIdInput = (
    <FormInput
      id={`${idPrefix}-asset-id${readOnly ? "-readonly" : ""}`}
      size="small"
      fullWidth
      value={readOnly ? (assets.asset ?? "") : lookupId}
      onChange={readOnly ? undefined : (e) => handleLookupIdChange(e.target.value)}
      onBlur={
        readOnly
          ? undefined
          : () => {
              const trimmedId = lookupId.trim();
              if (trimmedId && trimmedId !== lastFetchedAssetId) {
                fetchMetadata(trimmedId, { silent: true });
              }
            }
      }
      placeholder="Enter asset ID"
      error={!!errors.asset}
      disabled={readOnly}
      sx={{ width: "100%", minWidth: 0, maxWidth: "100%" }}
      slotProps={{
        htmlInput: {
          maxLength: IN_APP_NOTIFICATION_VALIDATIONS.assetId.maxLength,
        },
      }}
    />
  );

  const assetIdControl = readOnly ? (
    <Box sx={{ width: "100%", ...inputSx }}>{assetIdInput}</Box>
  ) : (
    <Box
      sx={{
        display: "flex",
        flexDirection: { xs: "column", sm: "row" },
        flexWrap: "wrap",
        gap: 0.75,
        alignItems: { xs: "stretch", sm: "flex-start" },
        width: "100%",
        minWidth: 0,
        maxWidth: page ? pageAssetIdControlRowSx.maxWidth : "100%",
      }}
    >
      <FormInput
        id={`${idPrefix}-asset-id`}
        size="small"
        fullWidth
        value={lookupId}
        onChange={(e) => handleLookupIdChange(e.target.value)}
        onBlur={() => {
          const trimmedId = lookupId.trim();
          if (trimmedId && trimmedId !== lastFetchedAssetId) {
            fetchMetadata(trimmedId, { silent: true });
          }
        }}
        placeholder="Enter asset ID"
        error={!!errors.asset}
        sx={{ flex: "1 1 160px", minWidth: 0, maxWidth: "100%" }}
        slotProps={{
          htmlInput: {
            maxLength: IN_APP_NOTIFICATION_VALIDATIONS.assetId.maxLength,
          },
        }}
      />
      <AppButton
        variant="primary"
        size="small"
        loading={fetchLoading}
        onMouseDown={preventBlurBeforeClick}
        onClick={handleFetchClick}
        sx={{ flexShrink: 0, alignSelf: { xs: "stretch", sm: "flex-start" }, whiteSpace: "nowrap" }}
      >
        Fetch Asset Data
      </AppButton>
    </Box>
  );

  const metadataBlock =
    displayTags.length > 0 ? (
      <Box sx={{ minWidth: 0, mt: page ? 0.75 : 0 }}>
        {displayTags.length > 1 && !readOnly && (
          <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.5 }}>
            Select a tag for this notification. Payload uses one assetTag and its resolutions per screen.
          </Typography>
        )}
        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75 }}>
          {displayTags.map((entry, index) => {
            const isSelected = assets.assetTag === entry.assetTag;
            const isSelectable = displayTags.length > 1 && !readOnly;

            return (
              <Box
                key={`${entry.assetTag || "entry"}-${index}`}
                onClick={() => {
                  if (isSelectable && entry.assetTag) {
                    onChange("assetTag", entry.assetTag);
                    onChange("resolutions", entry.resolutions || []);
                  }
                }}
                sx={{
                  p: 0.75,
                  borderRadius: 1,
                  border: "1px solid",
                  borderColor: isSelected ? "primary.main" : "divider",
                  bgcolor: isSelected ? "action.hover" : "transparent",
                  cursor: isSelectable && entry.assetTag ? "pointer" : "default",
                }}
              >
                <Typography variant="body2" sx={{ mb: entry.resolutions?.length ? 0.5 : 0, fontSize: "0.8125rem" }}>
                  <Typography component="span" fontWeight={600}>
                    Tag:
                  </Typography>{" "}
                  {entry.assetTag || "—"}
                </Typography>
                {entry.resolutions?.length > 0 && (
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                    {entry.resolutions.map((resolution) => (
                      <Chip key={resolution} label={resolution} size="small" variant="outlined" />
                    ))}
                  </Box>
                )}
              </Box>
            );
          })}
        </Box>
      </Box>
    ) : null;

  if (page && paired) {
    return (
      <>
        <Box sx={{ ...pageSubsectionLabelSx, gridArea: "assetLabel" }}>
          <Typography variant="body2" sx={pageFormLabelSx}>
            Asset ID
            {required && (
              <Typography component="span" color="error.main" sx={{ ml: 0.25 }}>
                *
              </Typography>
            )}
          </Typography>
        </Box>
        <Box
          sx={{
            ...pageSubsectionInputRowSx,
            gridArea: "assetControls",
            flexDirection: "column",
            alignItems: "flex-start",
            minHeight: 0,
            ...pageAssetIdControlRowSx,
          }}
        >
          <Box sx={{ ...pageSubsectionInputRowSx, minHeight: 40, width: "100%" }}>
            <Box sx={{ flex: 1, minWidth: 0 }}>{assetIdInput}</Box>
            {!readOnly && (
              <AppButton
                variant="primary"
                size="small"
                loading={fetchLoading}
                onMouseDown={preventBlurBeforeClick}
                onClick={handleFetchClick}
                sx={pageFormInlineActionSx}
              >
                Fetch Asset Data
              </AppButton>
            )}
          </Box>
          {errors.asset && (
            <Typography variant="caption" color="error" sx={{ display: "block", mt: 0.5 }}>
              {errors.asset}
            </Typography>
          )}
        </Box>
        {metadataBlock && (
          <Box sx={{ gridColumn: { xs: "1", md: "1 / -1" }, mt: 0.5 }}>{metadataBlock}</Box>
        )}
      </>
    );
  }

  if (page) {
    return (
      <Box sx={{ display: "flex", flexDirection: "column", height: "100%", width: "100%", minWidth: 0 }}>
        <Box sx={pageSubsectionLabelSx}>
          <Typography variant="body2" sx={pageFormLabelSx}>
            Asset ID
            {required && (
              <Typography component="span" color="error.main" sx={{ ml: 0.25 }}>
                *
              </Typography>
            )}
          </Typography>
        </Box>
        <Box sx={{ ...pageSubsectionInputRowSx, ...pageAssetIdControlRowSx }}>
          <Box sx={{ flex: 1, minWidth: 0 }}>{assetIdInput}</Box>
          {!readOnly && (
            <AppButton
              variant="primary"
              size="small"
              loading={fetchLoading}
              onMouseDown={preventBlurBeforeClick}
              onClick={handleFetchClick}
              sx={{ flexShrink: 0, whiteSpace: "nowrap", alignSelf: "center", ...pageFormInlineActionSx }}
            >
              Fetch Asset Data
            </AppButton>
          )}
        </Box>
        {errors.asset && (
          <Typography variant="caption" color="error" sx={{ display: "block", mt: 0.5 }}>
            {errors.asset}
          </Typography>
        )}
        {metadataBlock}
      </Box>
    );
  }

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: compact ? 1 : 1.25 }}>
      <FormFieldRow compact={fieldCompact} page={page} label="Asset ID" required={required} error={errors.asset}>
        {assetIdControl}
      </FormFieldRow>
      {metadataBlock && (
        <FormFieldRow compact={fieldCompact} page={page} label="Metadata">
          {metadataBlock}
        </FormFieldRow>
      )}
    </Box>
  );
};

export default AssetFields;
